// src/components/auth/Login.js
import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WelcomeHeader from '../layout/WelcomeHeader';
import styles from './Login.module.css';
import { authApi } from '../../api';
import { hasCookieConsent, COOKIE_CONSENT_EVENT } from '../../utils/cookieConsent';
import { applyPendingEmailOptIn } from '../../utils/pendingEmailOptIn';

function Login({ setLoggedIn, setUsername, setUserId, setRoleLevel }) {
  const { t } = useTranslation();
  const [cookieConsentGiven, setCookieConsentGiven] = useState(hasCookieConsent());
  const [username, setLocalUsername] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showAdminNotice, setShowAdminNotice] = useState(false);
  const [adminRoleName, setAdminRoleName] = useState('');
  const [needsVerification, setNeedsVerification] = useState(false);
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState(null);
  const adminRedirectTimer = useRef(null);
  const navigate = useNavigate();

  // Auf Cookie-Zustimmung reagieren (Banner kann während des Login-Besuchs akzeptiert werden)
  useEffect(() => {
    const onChange = () => setCookieConsentGiven(hasCookieConsent());
    window.addEventListener(COOKIE_CONSENT_EVENT, onChange);
    return () => window.removeEventListener(COOKIE_CONSENT_EVENT, onChange);
  }, []);

  // Rollen-Namen für Anzeige
  const getRoleName = (level) => {
    switch (level) {
      case 0: return 'Admin';
      case 1: return 'Moderator';
      default: return '';
    }
  };

  // Login mit API Service
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!cookieConsentGiven) return;
    setErrorMessage('');
    setNeedsVerification(false);
    setResendStatus(null);

    const result = await authApi.login({ username, password });

    if (result.success) {
      const userId = result.data?.user_id;

      // Bei Registrierung gewählte E-Mail-Präferenz jetzt anwenden (erster
      // erfolgreicher Login nach Bestätigung, siehe utils/pendingEmailOptIn.js)
      applyPendingEmailOptIn(username);

      // Nach Login die User-Daten holen
      const meResult = await authApi.getMe();
      const roleLevel = meResult.success ? (meResult.data?.role ?? 4) : 4;

      setUsername(username);
      if (userId) {
        setUserId(userId);
      }
      
      // RoleLevel setzen
      if (setRoleLevel) {
        setRoleLevel(roleLevel);
      }
      
      setLoggedIn(true);

      // Admin/Moderator Benachrichtigung anzeigen (roleLevel <= 1)
      if (roleLevel <= 1) {
        setAdminRoleName(getRoleName(roleLevel));
        setShowAdminNotice(true);
        // Nach 3 Sekunden automatisch zur Admin-Seite navigieren
        adminRedirectTimer.current = setTimeout(() => {
          navigate('/admin');
        }, 3000);
      } else {
        navigate('/mein-regal');
      }
    } else {
      console.error('❌ Login fehlgeschlagen:', result.error);
      if (result.error?.code === 'EMAIL_NOT_VERIFIED') {
        setNeedsVerification(true);
        setErrorMessage(t('auth.emailNotVerified'));
      } else {
        setErrorMessage(result.error?.message || t('auth.loginFailed'));
      }
    }
  };

  const handleResendVerification = async (e) => {
    e.preventDefault();
    if (!resendEmail.trim()) return;
    setResendStatus('sending');
    const result = await authApi.resendVerification(resendEmail.trim());
    if (result.success) {
      setResendStatus('sent');
    } else if (result.error?.code === 'RESEND_COOLDOWN') {
      setResendStatus('cooldown');
    } else {
      setResendStatus('error');
    }
  };

  // Admin-Benachrichtigung
  if (showAdminNotice) {
    return (
      <>
        <WelcomeHeader />
        <div className={styles.authContainer}>
          <div className={styles.authCard}>
            <div className={styles.adminNotice}>
              <div className={styles.adminIcon}>👑</div>
              <h2>{t('auth.adminWelcome', { role: adminRoleName })}</h2>
              <p>{t('auth.adminPermissions')}</p>
              <p className={styles.adminHint}>
                {t('auth.adminRedirecting')}
              </p>
              <div className={styles.adminButtons}>
                <button 
                  className={styles.adminButton}
                  onClick={() => {
                    clearTimeout(adminRedirectTimer.current);
                    navigate('/admin');
                  }}
                >
                  🛠️ {t('auth.goToAdminPanel')}
                </button>
                <button 
                  className={styles.skipButton}
                  onClick={() => {
                    clearTimeout(adminRedirectTimer.current);
                    navigate('/mein-regal');
                  }}
                >
                  {t('auth.goToMyShelf')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <WelcomeHeader />
      <div className={styles.authContainer}>
      <div className={styles.authCard}>
        {/* Logo */}
        <div className={styles.authLogo}>
          <img src={require('../../assets/logo_1.png')} alt="MangaShelf" className={styles.authLogoImg} />
        </div>

        {/* Header */}
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>{t('auth.loginTitle')}</h1>
          <p className={styles.authSubtitle}>{t('auth.loginSubtitle')}</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className={styles.authForm}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('auth.username')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setLocalUsername(e.target.value)}
              required
              className={styles.formInput}
              placeholder={t('auth.usernamePlaceholder')}
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('auth.password')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={styles.formInput}
              placeholder={t('auth.passwordPlaceholder')}
            />
            <button
              type="button"
              className={styles.linkButton}
              style={{ alignSelf: 'flex-end', fontSize: 'var(--font-size-sm)' }}
              onClick={() => navigate('/forgot-password')}
            >
              {t('auth.forgotPassword')}
            </button>
          </div>

          {errorMessage && (
            <div className={styles.errorMessage}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
              {errorMessage}
            </div>
          )}

          {needsVerification && (
            <div className={styles.formGroup}>
              {resendStatus === 'sent' ? (
                <p className={styles.authSubtitle}>{t('auth.verificationResent')}</p>
              ) : (
                <>
                  <label className={styles.formLabel}>{t('auth.resendEmailLabel')}</label>
                  <input
                    type="email"
                    value={resendEmail}
                    onChange={(e) => setResendEmail(e.target.value)}
                    className={styles.formInput}
                    placeholder={t('auth.resendEmailPlaceholder')}
                  />
                  <button
                    type="button"
                    className={styles.linkButton}
                    onClick={handleResendVerification}
                    disabled={resendStatus === 'sending' || !resendEmail.trim()}
                  >
                    {resendStatus === 'sending' ? t('auth.sending') : t('auth.resendVerification')}
                  </button>
                  {resendStatus === 'cooldown' && (
                    <p className={styles.authSubtitle}>{t('auth.resendCooldown')}</p>
                  )}
                  {resendStatus === 'error' && (
                    <p className={styles.authSubtitle}>{t('auth.resendError')}</p>
                  )}
                </>
              )}
            </div>
          )}

          {!cookieConsentGiven && (
            <p className={styles.cookieConsentHint}>{t('auth.cookieConsentRequired')}</p>
          )}

          <button type="submit" className={styles.submitButton} disabled={!cookieConsentGiven}>
            {t('auth.signIn')}
          </button>
        </form>

        {/* Divider */}
        <div className={styles.divider}>
          <span>{t('auth.or')}</span>
        </div>

        {/* Actions */}
        <div className={styles.authActions}>
          <p className={styles.authPrompt}>
            {t('auth.noAccount')} <button onClick={() => navigate('/register')} className={styles.linkButton}>{t('auth.signUp')}</button>
          </p>
          <button onClick={() => navigate('/')} className={styles.backButton}>
            {t('auth.backToHome')}
          </button>
        </div>
      </div>
    </div>
    </>
  );
}

export default Login;
